import React, { useCallback, useEffect, useState } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

/**
 * The one list on this page that is a job of work, not a number to look at.
 *
 * Two things land here: a sale that could not take everything it needed
 * (something has to be made or fitted), and hardware that has run low. Both
 * are "go and do this", so they share one list rather than two panels.
 */
export default function TodoCard({ store, storeName, onNavigate }) {
  const [shortfalls, setShortfalls] = useState([]);
  const [lowHardware, setLowHardware] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    // Each half stands on its own — if one call fails the other still shows.
    fetch(`${API_URL}/api/shortfalls?store=${store}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setShortfalls(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`${API_URL}/api/hardware/reorder?store=${store}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setLowHardware(d && Array.isArray(d.items) ? d.items.filter((i) => i.needsOrder) : []))
      .catch(() => {});
  }, [store]);

  useEffect(() => { load(); }, [load]);

  const resolve = (row) => {
    setBusy(row.id);
    const who = (() => {
      try { return (JSON.parse(sessionStorage.getItem("nycmfg-user")) || {}).name || ""; } catch { return ""; }
    })();
    fetch(`${API_URL}/api/shortfalls/${row.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "resolved", resolvedBy: who }),
    })
      .then((r) => { if (r.ok) setShortfalls((s) => s.filter((x) => x.id !== row.id)); })
      .catch(() => {})
      .finally(() => setBusy(null));
  };

  const count = shortfalls.length + lowHardware.length;
  const bar = (color) => ({ width: 4, alignSelf: "stretch", background: color, flexShrink: 0 });
  const rowStyle = { padding: "10px 20px 10px 0", borderBottom: "1px solid rgba(10,10,15,0.12)", display: "flex", alignItems: "center", gap: 14 };

  return (
    <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", marginBottom: 24 }}>
      <div style={{ padding: "12px 20px", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>
          To do
          {count > 0 && <span style={{ ...S.jobCountBadge, marginLeft: 8 }}>{count}</span>}
        </span>
        <span style={{ fontSize: 12, color: "rgba(10,10,15,0.45)" }}>{storeName}</span>
      </div>

      {count === 0 && (
        <div style={{ padding: 30, textAlign: "center", color: "#34d399", fontSize: 14 }}>All clear.</div>
      )}

      {shortfalls.map((f) => (
        <div key={`sf-${f.id}`} style={rowStyle}>
          <div style={bar("#ef4444")} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>
              {f.productName || "A product"} sold{f.channel ? ` on ${f.channel}` : ""}
              {f.orderId && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "rgba(10,10,15,0.45)", marginLeft: 8 }}>
                  {f.orderId}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: "#ef4444" }}>
              {(f.shortItems || []).map((i) => `${i.short} × ${i.name}`).join(" · ")} short
              <span style={{ color: "rgba(10,10,15,0.58)" }}>
                {f.kind === "bundle" ? " — make it and put it in stock." : " — fit it when you have more."}
              </span>
            </div>
          </div>
          <button
            onClick={() => resolve(f)}
            disabled={busy === f.id}
            style={{ ...S.addBtn, padding: "5px 12px", fontSize: 12, opacity: busy === f.id ? 0.5 : 1, whiteSpace: "nowrap" }}
          >
            {f.kind === "bundle" ? "Made it" : "Got it"}
          </button>
        </div>
      ))}

      {lowHardware.map((h) => (
        <div key={`hw-${h.id}`} style={rowStyle}>
          <div style={bar("#f59e0b")} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>{h.name}</div>
            <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{h.quantity}</span> left
              {h.lowAt != null && <>, low at <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{h.lowAt}</span></>}
              {h.daysLeft != null && <> · about <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{h.daysLeft}</span> days left</>}
              {h.suggestPacks > 0 && ` · buy ${h.suggestPacks} pack${h.suggestPacks !== 1 ? "s" : ""}`}
            </div>
          </div>
          {h.buyUrl ? (
            <a
              href={h.buyUrl}
              target="_blank"
              rel="noreferrer"
              style={{ ...S.addBtn, display: "inline-flex", padding: "5px 12px", fontSize: 12, textDecoration: "none", whiteSpace: "nowrap" }}
            >
              Buy
            </a>
          ) : (
            <button
              onClick={() => onNavigate && onNavigate("store-supplies")}
              style={{ ...S.clearBtn, padding: "5px 12px", fontSize: 12, whiteSpace: "nowrap" }}
            >
              See list
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
